import { useEffect, useState } from 'react';
import {
  useApi,
  identityApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import RefreshIcon from '@material-ui/icons/Refresh';
import { InfoCard, Progress } from '@backstage/core-components';
import { spassuCodeApiRef } from '../../api';
import { WorkerStatusIndicator } from '../WorkerStatusIndicator';

const useStyles = makeStyles(theme => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  tokenField: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
  },
  copyButton: {
    padding: theme.spacing(0.5),
  },
  instructions: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  codeBlockWrapper: {
    position: 'relative' as const,
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.background.default
        : theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  codeBlock: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    padding: theme.spacing(2),
    paddingRight: theme.spacing(6),
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  codeBlockCopyButton: {
    position: 'absolute' as const,
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

export const WorkerTokenSettingsTab = () => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);

  const [token, setToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [tokenTtlSeconds, setTokenTtlSeconds] = useState<number | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState<'token' | 'userId' | 'command' | null>(
    null,
  );

  const appBaseUrl = configApi.getString('app.baseUrl');
  const workerServerUrl = (() => {
    try {
      const url = new URL(appBaseUrl);
      if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
        return 'ws://localhost:7009';
      }
      return `https://${url.hostname}/ws/`;
    } catch {
      return 'ws://localhost:7009';
    }
  })();

  // Buscar userId ao montar para preencher o comando local mesmo sem token gerado.
  useEffect(() => {
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        setUserId(identity.userEntityRef);
      })
      .catch(() => {});
  }, [identityApi]);

  const generateToken = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getWorkerToken();
      setToken(result.token);
      setUserId(result.userId);
      setTokenTtlSeconds(result.ttlSeconds);
      setTokenExpiresAt(result.expiresAt);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao gerar token');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (
    text: string,
    field: 'token' | 'userId' | 'command',
  ) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // fallback
    }
  };

  const maskedToken = token
    ? `${token.substring(0, 8)}${'*'.repeat(
        Math.max(0, token.length - 12),
      )}${token.substring(token.length - 4)}`
    : '';

  const workerCommandLines = [
    'yarn workspace @internal/spassu-code-worker start \\',
    `  --server ${workerServerUrl} \\`,
    token && userId ? `  --token ${token} \\` : '  --token <TOKEN> \\',
    token && userId
      ? `  --user-id "${userId}" \\`
      : '  --user-id "<USER_ID>" \\',
    '  --skills-plugin-path "$(pwd)/.spassu"',
  ];
  const workerCommand = workerCommandLines.join('\n');
  let tokenHelperText = 'Token gerado. Renove quando expirar.';
  if (copied === 'token') {
    tokenHelperText = 'Copiado!';
  } else if (tokenTtlSeconds && tokenExpiresAt) {
    tokenHelperText = `Token expira em ${Math.floor(
      tokenTtlSeconds / 3600,
    )}h (${new Date(tokenExpiresAt).toLocaleString('pt-BR')}).`;
  }

  return (
    <Grid container direction="column" spacing={3}>
      <Grid item>
        <InfoCard
          title="Worker Token"
          subheader="Gere um token para autenticar seu worker local"
        >
          <div className={classes.statusRow}>
            <Typography variant="body2">Status:</Typography>
            <WorkerStatusIndicator />
          </div>

          {loading && <Progress />}

          {error && (
            <Typography color="error" variant="body2" className={classes.field}>
              {error}
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={generateToken}
            disabled={loading}
            startIcon={<RefreshIcon />}
          >
            {token ? 'Gerar Novo Token' : 'Gerar Token'}
          </Button>

          {token && userId && (
            <Grid container spacing={2} style={{ marginTop: 16 }}>
              <Grid item xs={12}>
                <TextField
                  label="User ID"
                  value={userId}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                    className: classes.tokenField,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          className={classes.copyButton}
                          onClick={() => copyToClipboard(userId, 'userId')}
                          title="Copiar"
                          size="small"
                        >
                          <FileCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    copied === 'userId'
                      ? 'Copiado!'
                      : 'Seu identificador no Backstage'
                  }
                  className={classes.field}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Token"
                  value={showToken ? token : maskedToken}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                    className: classes.tokenField,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          className={classes.copyButton}
                          onClick={() => setShowToken(!showToken)}
                          title={showToken ? 'Ocultar' : 'Mostrar'}
                          size="small"
                        >
                          {showToken ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                        <IconButton
                          className={classes.copyButton}
                          onClick={() => copyToClipboard(token, 'token')}
                          title="Copiar"
                          size="small"
                        >
                          <FileCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText={tokenHelperText}
                  className={classes.field}
                />
              </Grid>
            </Grid>
          )}
        </InfoCard>
      </Grid>

      {token && userId && (
        <Grid item>
          <InfoCard
            title="Como usar"
            subheader="Inicie o worker local no seu terminal"
          >
            <Typography variant="body2" gutterBottom>
              Execute o seguinte comando no terminal para conectar o worker
              local:
            </Typography>
            <div className={classes.codeBlockWrapper}>
              <div className={classes.codeBlock}>{workerCommand}</div>
              <IconButton
                className={classes.codeBlockCopyButton}
                onClick={() => copyToClipboard(workerCommand, 'command')}
                title={copied === 'command' ? 'Copiado!' : 'Copiar comando'}
                size="small"
              >
                <FileCopyOutlinedIcon fontSize="small" />
              </IconButton>
            </div>

            <div className={classes.instructions}>
              <Typography variant="subtitle2" gutterBottom>
                Requisitos:
              </Typography>
              <Typography variant="body2" component="ul">
                <li>Node.js 24+ instalado</li>
                <li>Yarn 4.4.1+ instalado</li>
                <li>
                  <code>claude login</code> executado (Claude Team account)
                </li>
                <li>
                  Acesso de rede ao servidor Backstage (porta WebSocket 7009)
                </li>
                <li>
                  Executar a partir da raiz do repositório clonado (onde existe
                  o diretório <code>.spassu/</code>)
                </li>
              </Typography>
            </div>
          </InfoCard>
        </Grid>
      )}
    </Grid>
  );
};
